import { useState, useEffect, useCallback } from 'react';
import { IndexedDBService } from '../services/storage/IndexedDBService';
import { ledgerApi } from '../services/api/endpoints/ledger';
import { mockAccounts, mockJournalEntries } from '../data/mockData';
import type { Account, JournalEntry } from '../types/accounting';
import type { 
  LedgerFilters, 
  AccountMovementSearchParams, 
  TrialBalanceParams,
  TrialBalanceEntry
} from '../types/filters';

export function useLedger() {
  const [trialBalance, setTrialBalance] = useState<TrialBalanceEntry[]>([]);
  const [accountMovements, setAccountMovements] = useState<Record<string, JournalEntry[]>>({});
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState<LedgerFilters>({});

  useEffect(() => {
    loadTrialBalance();
  }, []);

  const loadTrialBalance = async (params?: TrialBalanceParams) => {
    try {
      setLoading(true);
      
      // Try API first if online
      if (navigator.onLine) {
        try {
          const response = await ledgerApi.getTrialBalance(params);
          if (response.success && response.data) {
            // Protection contre les données non-array de l'API
            const apiData = Array.isArray(response.data) ? response.data : [];
            setTrialBalance(apiData);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error loading trial balance from API:', error);
        }
      }

      // Fallback to local calculation
      const localAccounts = await IndexedDBService.getAll<Account>('accounts');
      const localEntries = await IndexedDBService.getAll<JournalEntry>('journal_entries');

      // Si pas de données locales, utiliser les données de démo
      const accounts = localAccounts.length > 0 ? localAccounts : mockAccounts;
      let entries = localEntries.length > 0 ? localEntries : mockJournalEntries;

      // Calculer la balance avec filtres
      let filteredAccounts = [...accounts];
      
      if (params?.date) {
        // Filtrer les entrées jusqu'à la date spécifiée
        const filterDate = new Date(params.date);
        entries = entries.filter(entry => new Date(entry.date) <= filterDate);
      }

      const balance: TrialBalanceEntry[] = filteredAccounts.map(account => {
        const accountEntries = entries.filter(entry =>
          entry.lines.some(line => line.accountId === account.id)
        );

        const totals = accountEntries.reduce((acc, entry) => {
          entry.lines.forEach(line => {
            if (line.accountId === account.id) {
              acc.debit += line.debit || 0;
              acc.credit += line.credit || 0;
            }
          });
          return acc;
        }, { debit: 0, credit: 0 });

        return {
          account: {
            id: account.id,
            code: account.code,
            name: account.name,
            type: account.type
          },
          debit: totals.debit,
          credit: totals.credit,
          balance: totals.debit - totals.credit
        };
      });

      setTrialBalance(balance);
    } catch (error) {
      console.error('Error loading trial balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAccountMovements = async (accountId: string, params?: AccountMovementSearchParams) => {
    try {
      setLoading(true);

      // Try API first if online
      if (navigator.onLine) {
        try {
          const response = await ledgerApi.getAccountMovements(accountId, params);
          if (response.success && response.data?.data) {
            setAccountMovements(prev => ({
              ...prev,
              [accountId]: response.data!.data
            }));
            setPagination({
              page: response.data!.page,
              pageSize: response.data!.pageSize,
              total: response.data!.total,
              totalPages: response.data!.totalPages
            });
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error loading account movements from API:', error);
        }
      }

      // Fallback to local data
      const localEntries = await IndexedDBService.getAll<JournalEntry>('journal_entries');
      const entries = localEntries.length > 0 ? localEntries : mockJournalEntries;
      
      let filteredMovements = entries
        .filter(entry => entry.lines.some(line => line.accountId === accountId));

      // Apply filters
      if (params?.startDate) {
        filteredMovements = filteredMovements.filter(entry => 
          entry.date >= params.startDate!
        );
      }
      
      if (params?.endDate) {
        filteredMovements = filteredMovements.filter(entry => 
          entry.date <= params.endDate!
        );
      }
      
      if (params?.journalType && params.journalType !== 'all') {
        filteredMovements = filteredMovements.filter(entry => 
          entry.journalType === params.journalType
        );
      }
      
      if (params?.status && params.status !== 'all') {
        filteredMovements = filteredMovements.filter(entry => 
          entry.status === params.status
        );
      }

      // Sorting
      if (params?.sortBy) {
        filteredMovements.sort((a, b) => {
          const order = params.sortOrder === 'desc' ? -1 : 1;
          if (params.sortBy === 'date') {
            return (new Date(a.date).getTime() - new Date(b.date).getTime()) * order;
          } else if (params.sortBy === 'amount') {
            const aAmount = a.totalDebit || a.totalCredit;
            const bAmount = b.totalDebit || b.totalCredit;
            return (aAmount - bAmount) * order;
          } else if (params.sortBy === 'reference') {
            return a.reference.localeCompare(b.reference) * order;
          }
          return 0;
        });
      }

      // Pagination
      const page = params?.page || 1;
      const pageSize = params?.pageSize || 50;
      const total = filteredMovements.length;
      const totalPages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      
      setAccountMovements(prev => ({
        ...prev,
        [accountId]: filteredMovements.slice(startIndex, endIndex)
      }));
      
      setPagination({
        page,
        pageSize,
        total,
        totalPages
      });
    } catch (error) {
      console.error('Error loading account movements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAccountBalance = async (accountId: string, date?: string) => {
    try {
      if (navigator.onLine) {
        const response = await ledgerApi.getAccountBalance(accountId, { date });
        if (response.success && response.data) {
          return response.data;
        }
      }

      // Fallback to local calculation
      const localEntries = await IndexedDBService.getAll<JournalEntry>('journal_entries');
      const entries = localEntries.length > 0 ? localEntries : mockJournalEntries;
      
      let relevantEntries = entries.filter(entry =>
        entry.lines.some(line => line.accountId === accountId)
      );

      if (date) {
        const filterDate = new Date(date);
        relevantEntries = relevantEntries.filter(entry => 
          new Date(entry.date) <= filterDate
        );
      }

      const totals = relevantEntries.reduce((acc, entry) => {
        entry.lines.forEach(line => {
          if (line.accountId === accountId) {
            acc.debit += line.debit || 0;
            acc.credit += line.credit || 0;
          }
        });
        return acc;
      }, { debit: 0, credit: 0 });

      return {
        accountId,
        debit: totals.debit,
        credit: totals.credit,
        balance: totals.debit - totals.credit
      };
    } catch (error) {
      console.error('Error getting account balance:', error);
      return {
        accountId,
        debit: 0,
        credit: 0,
        balance: 0
      };
    }
  };

  const applyFilters = useCallback((newFilters: LedgerFilters) => {
    setFilters(newFilters);
    const params: TrialBalanceParams = {
      date: newFilters.startDate,
      mode: newFilters.mode,
      currency: newFilters.currency
    };
    loadTrialBalance(params);
  }, []);

  return {
    trialBalance,
    accountMovements,
    loading,
    pagination,
    filters,
    loadTrialBalance,
    loadAccountMovements,
    getAccountBalance,
    applyFilters
  };
}
